import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, AlertTriangle, ChevronDown, ChevronRight, Upload, FileArchive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConfirmModal from '@/components/shared/ConfirmModal';
import axios from 'axios';
import { logAction } from '@/lib/activityTracker';

const API_BASE = 'https://xceededge.com';

const getStatus = (a) => {
  if (a.submitted) return 'submitted';
  if (a.due_date && new Date(a.due_date) < new Date()) return 'overdue';
  return 'pending';
};

const statusConfig = {
  submitted: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, iconColor: 'text-green-500', label: 'Submitted' },
  pending:   { color: 'bg-blue-100 text-blue-700',   icon: Clock,        iconColor: 'text-blue-500',  label: 'Pending' },
  overdue:   { color: 'bg-red-100 text-red-700',     icon: AlertTriangle, iconColor: 'text-red-500',  label: 'Overdue' },
};

export default function AssignmentsSection({ course, user }) {
  const rawAssignments = course?.assignments || [];

  // Track submission state locally so UI updates without a page reload
  const [localSubs, setLocalSubs] = useState({}); // { [assignmentId]: { submitted, submitted_at, submitted_filename } }
  const [expanded, setExpanded] = useState(new Set());
  const [filter, setFilter] = useState('all');

  // Upload state
  const fileInputRef    = useRef(null);
  const [activeId, setActiveId]       = useState(null);  // assignment being submitted
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);

  const assignments = rawAssignments.map(a => ({
    ...a,
    ...(localSubs[a.id] || {}),
  }));

  const filtered = filter === 'all'
    ? assignments
    : assignments.filter(a => getStatus(a) === filter);

  const submitted  = assignments.filter(a => a.submitted).length;
  const toggleExpand = (id) => setExpanded(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const openFilePicker = (assignmentId) => {
    setActiveId(assignmentId);
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Please upload a .zip file only.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      alert('File must be under 50 MB.');
      return;
    }
    setSelectedFile(file);
    setShowConfirm(true);
  };

  const handleUpload = async () => {
    setShowConfirm(false);
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('session', user?.session || '');
    formData.append('assignment_id', activeId);
    formData.append('file', selectedFile);

    try {
      const res = await axios.post(`${API_BASE}/submit-assignment.php`, formData, {
        onUploadProgress: (e) => setProgress(Math.round(e.loaded * 100 / e.total)),
      });
      if (res.data.success) {
        const assignment = rawAssignments.find(a => a.id === activeId);
        logAction('assignment_submitted', {
          assignment_id: activeId,
          assignment_title: assignment?.title || null,
          filename: selectedFile.name,
        }, {
          courseId: course?.course_id || course?.id,
        });
        setLocalSubs(prev => ({
          ...prev,
          [activeId]: {
            submitted:          true,
            submitted_at:       new Date().toISOString(),
            submitted_filename: selectedFile.name,
          },
        }));
      } else {
        alert(res.data.message || 'Submission failed');
      }
    } catch {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
      setSelectedFile(null);
      setActiveId(null);
    }
  };

  const activeAssignment = assignments.find(a => a.id === activeId);

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept=".zip" className="hidden" onChange={handleFileChange} />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Assignments</h2>
          <p className="text-muted-foreground text-sm mt-1">{submitted}/{assignments.length} submitted</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'submitted', 'pending', 'overdue'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all ${
                filter === f
                  ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                  : 'glass border border-black/10 text-muted-foreground hover:text-foreground'
              }`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="glass rounded-2xl border border-primary/30 p-4">
          <p className="text-sm font-medium mb-2">Uploading… {progress}%</p>
          <div className="h-2 bg-black/10 rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-200 rounded-full" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="glass rounded-2xl border border-white/80 p-8 text-center">
          <p className="text-muted-foreground text-sm">No assignments found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a, i) => {
            const status    = getStatus(a);
            const s         = statusConfig[status];
            const StatusIcon = s.icon;
            const isOpen    = expanded.has(a.id);

            return (
              <motion.div key={a.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="glass rounded-2xl border border-white/80 hover:border-primary/20 transition-colors overflow-hidden">

                {/* Header row */}
                <div className="p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    status === 'submitted' ? 'bg-green-100' : status === 'overdue' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    <StatusIcon className={`w-5 h-5 ${s.iconColor}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-sm">{a.topic}</h3>
                    {a.subtopic && <p className="text-xs text-muted-foreground">{a.subtopic}</p>}
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                      {a.module && <span>{a.module}</span>}
                      {a.due_date && (
                        <span className={`flex items-center gap-1 ${status === 'overdue' ? 'text-red-500 font-semibold' : ''}`}>
                          <Clock className="w-3 h-3" />
                          Due {new Date(a.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {a.total_points && <span>{a.total_points} pts</span>}
                      {a.submitted_filename && (
                        <span className="flex items-center gap-1 text-green-600">
                          <FileArchive className="w-3 h-3" /> {a.submitted_filename}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.color}`}>{s.label}</span>

                    {/* Expand/collapse questions */}
                    {(a.questions?.length > 0 || a.description) && (
                      <button onClick={() => toggleExpand(a.id)}
                        className="p-1.5 hover:bg-black/5 rounded-lg transition">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Submit / Resubmit button */}
                    <Button size="sm"
                      disabled={uploading}
                      onClick={() => openFilePicker(a.id)}
                      className={`gap-1.5 rounded-xl text-xs font-semibold shadow-sm ${
                        a.submitted
                          ? 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                          : 'bg-primary text-primary-foreground shadow-primary/25 hover:bg-primary/90'
                      }`}>
                      <Upload className="w-3.5 h-3.5" />
                      {a.submitted ? 'Resubmit' : 'Submit'}
                    </Button>
                  </div>
                </div>

                {/* Questions panel */}
                {isOpen && (
                  <div className="border-t border-black/5 px-5 pb-5 pt-3 space-y-3">
                    {a.description && (
                      <p className="text-sm text-muted-foreground">{a.description}</p>
                    )}
                    {a.questions?.length > 0 && (
                      <ol className="space-y-2 pl-1">
                        {a.questions.map((q, qi) => (
                          <li key={q.id} className="flex gap-2.5">
                            <span className="text-xs font-bold text-primary pt-0.5 w-5 flex-shrink-0">{qi + 1}.</span>
                            <span className="text-sm text-foreground">{q.question_text}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                    <p className="text-xs text-muted-foreground italic">
                      Complete all questions above and upload your answers as a single .zip file (max 50 MB).
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Confirm upload modal */}
      <ConfirmModal
        open={showConfirm}
        title="Submit Assignment?"
        description={`Upload "${selectedFile?.name}" for "${activeAssignment?.topic}"?${activeAssignment?.submitted ? ' This will replace your previous submission.' : ''}`}
        confirmLabel="Yes, Submit"
        onConfirm={handleUpload}
        onCancel={() => { setShowConfirm(false); setSelectedFile(null); setActiveId(null); }}
      />
    </div>
  );
}
